import {
  Avatar,
  Button,
  Col,
  Flex,
  Input,
  Row,
  Table,
  Tag,
  Typography,
} from "antd";
import React, { useState } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Spinner from "../../../../../common/spinner/Spinner";
import {
  dateObjToDispViewDate,
  downloadUrl,
} from "../../../../../../utils/utils";
import styles from "./Payroll.module.scss";
import { colors } from "../../../../../../constants/colors";
import {
  getPayrollAction,
  runPayrollAction,
} from "../../../../../../redux/services/payroll/payrollServices";
import { appAssetsConfig } from "../../../../../../constants/config/appAssetsConfig";
import useDebounce from "../../../../../../hooks/useDebounce";
import { SearchOutlined } from "@ant-design/icons";
import { appConstConfig } from "../../../../../../constants/config/appConstConfig";
import ViewBankDetails from "./ViewBankDetails";
import ConfirmManualPayroll from "./ConfirmManualPayroll";
import { UserOutlined } from "@ant-design/icons";

const Payroll = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  /* loading dependencies */

  /* table dependencies */
  const [columns, setColumns] = useState([]);
  const { payroll } = useSelector((state) => state.payroll);
  const [searchValue, setSearchValue] = useState("");
  const [payoutDetailsDialog, setPayoutDetailsDialog] = useState({
    open: false,
    data: null,
  });

  const [manualPayrollDialog, setManualPayrollDialog] = useState({
    open: false,
    data: null,
  });

  const { debouncedValue: debouncedSearchValue, debounceLoading } = useDebounce(
    searchValue,
    500
  );
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
  });
  const paginationObj = {
    total: payroll?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };
  /* table dependencies */

  /* function definition to get table data */
  const handleGetTableData = () => {
    const dataToBeSent = {
      page: debouncedSearchValue !== "" ? 1 : pagination?.pageNo,
      limit: pagination?.pageSize,
      search: debouncedSearchValue,
    };
    dispatch(getPayrollAction(dataToBeSent, setLoading));
  };
  /* function definition to get table data */

  useEffect(() => {
    handleGetTableData();
  }, [pagination, debouncedSearchValue]);

  /* initial dependencies rendering */
  useEffect(() => {
    /* table column config */
    const tempTableColumns = [
      {
        title: t("Modules.Provider.serviceProviderId"),
        dataIndex: "organization_number",
        key: "organization_number",
        render: (_, record) => (
          <p>{record?.organization?.organization_number}</p>
        ),
      },
      {
        title: t("Modules.Provider.serviceProviderName"),
        key: "name",
        render: (_, record) => {
          return (
            <Flex gap="1rem" align="center">
              <Avatar
                src={record?.organization?.logo_image}
                key={record?.organization?._id}
                icon={<UserOutlined />}
              />
              {`${record?.organization?.name || "-"}`}
            </Flex>
          );
        },
      },
      {
        title: t("ProfileSetup.branchLabel"),
        dataIndex: "branch",
        key: "branch",
        render: (_, record) => <p>{record?.organization?.branch || "-"}</p>,
      },
      {
        title: t("revnue.revenue"),
        dataIndex: "total_revenue",
        key: "total_revenue",
        render: (_, record) => (
          <p>{record?.total_revenue ? record?.total_revenue?.toFixed(2) : "-"}</p>
        ),
      },
      {
        title: t("revenueShare"),
        dataIndex: "organization_revenue_share",
        key: "organization_revenue_share",
        render: (_, record) => (
          <p>{record?.organization_revenue_share ? record?.organization_revenue_share?.toFixed(2) : "-"}</p>
        ),
      },
      {
        title: t("dashboard.status"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              <Tag
                bordered={false}
                style={{
                  background:
                    record?.status === "PAID"
                      ? colors.lightTeal
                      : colors.lightPinkishRed,
                  color:
                    record?.status === "PAID"
                      ? colors.darkTeal
                      : colors.darkMaroon,
                }}
              >
                {record?.status}
              </Tag>
            </>
          );
        },
      },
      {
        title: t("userPermission.action"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              {record?.status === "DUE" ? (
                <Flex gap={5}>
                  <Button
                    disabled={record?.isRunPayroll}
                    size="small"
                    onClick={(e) => handleRunPayroll(e, record)}
                    ghost
                    type="primary"
                  >
                    {t("runPayroll")}
                  </Button>
                  <Button
                    disabled={record?.isRunPayroll}
                    size="small"
                    onClick={(e) => handleManualPayrolltDetails(e, record)}
                    ghost
                    type="primary"
                  >
                    {t("runManualPayroll")}
                  </Button>
                </Flex>
              ) : (
                <Flex gap={5}>
                  <Button
                    disabled={true}
                    size="small"
                    onClick={(e) => handleRunPayroll(e, record)}
                    type="primary"
                  >
                    {t("runPayroll")}
                  </Button>
                  <Button
                    disabled={true}
                    size="small"
                    onClick={(e) => handleManualPayrolltDetails(e, record)}
                    type="primary"
                  >
                    {t("runManualPayroll")}
                  </Button>
                </Flex>
              )}
            </>
          );
        },
      },
      {
        title: t("viewPdf"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              {record?.status === "PAID" ? (
                <Typography.Link onClick={(e) => handleViewDetails(e, record)}>
                  {t("view")}
                </Typography.Link>
              ) : (
                <>-</>
              )}
            </>
          );
        },
      },
      {
        title: t("payrollDate"),
        dataIndex: "payroll_date",
        key: "payroll_date",
        align: "center",
        render: (_, record) => (
          <p>
            {dateObjToDispViewDate(record?.payroll_date, "ddmmyyyy") || "-"}
          </p>
        ),
      },
      {
        title: t("viewPayoutDetails"),
        dataIndex: "view_bank",
        align: "center",
        key: "view_bank",
        render: (_, record) => (
          <>
            {record?.organization?.payment_type === "bank" &&
            record?.organization?.payout_details &&
            Object.keys(record?.organization?.payout_details)?.length > 0 ? (
              <Typography.Link
                onClick={(e) => handleViewPayoutDetails(e, record)}
              >
                {t("view")}
              </Typography.Link>
            ) : (
              <>-</>
            )}
          </>
        ),
      },
    ];
    /* table column config */
    setColumns(tempTableColumns);
  }, []);
  /* initial dependencies rendering */

  const handleRunPayroll = (e, row) => {
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedPayrollId = row?._id;
      const dataToBeSent = { selectedPayrollId };
      dispatch(runPayrollAction(dataToBeSent, setLoading));
    }
  };

  const handleViewDetails = (e, record) => {
    const link = document.createElement("a");
    link.href = record?.payroll_statement_url;
    link.download = "Invoice.pdf";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleViewPayoutDetails = (e, record) => {
    setPayoutDetailsDialog({
      open: true,
      data: record?.organization?.payout_details,
    });
  };

  const closePayoutDialog = () => {
    setPayoutDetailsDialog({
      open: false,
      data: null,
    });
  };

  const handleManualPayrolltDetails = (e, record) => {
    setManualPayrollDialog({
      open: true,
      data: record,
    });
  };

  const closeManualPayrollDialog = () => {
    setManualPayrollDialog({
      open: false,
      data: null,
    });
  };

  return (
    <div className={styles.userContainer}>
      <Flex vertical gap="2rem">
        <Row gutter={24}>
          <Col span={8} style={{ display: "flex", alignItems: "center" }}>
            <Input
              size="large"
              suffix={<SearchOutlined />}
              placeholder={t("CommonUtils.searchByName")}
              onChange={(event) => setSearchValue(event.target.value)}
              style={{ width: "100%" }}
            />
          </Col>
        </Row>
        {loading || debounceLoading ? (
          <Spinner />
        ) : (
          <Table
            dataSource={payroll?.data}
            columns={columns}
            pagination={paginationObj}
          />
        )}
      </Flex>
      <ViewBankDetails
        open={payoutDetailsDialog?.open}
        onClose={closePayoutDialog}
        data={payoutDetailsDialog?.data}
      />
      <ConfirmManualPayroll
        open={manualPayrollDialog?.open}
        data={manualPayrollDialog?.data}
        onClose={closeManualPayrollDialog}
        handleGetTableData={handleGetTableData}
      />
    </div>
  );
};

export default Payroll;
